import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  intl, HTTP_STATUS, DmsTable, DmsTableDeleteButton, DmsButtonGroup, DmsFlexContainer, fetchData, DmsButton, fetchUrl, getToken, DmsCheckBox
} from "dmsbasereact";
import Swal from "sweetalert2";
import { DmsUploadOnly } from "../../../components/DmsUploadOnly/DmsUploadOnly";
import { DmsTableEditTrainingMaterialButton } from "../../../components/DmsTableEditTrainingMaterialButton/DmsTableEditTrainingMaterialButton";
import { APP_ROUTES } from "../../../utils/NamedRoutes";
import { Select, MenuItem, InputLabel, FormControl, Typography, Box, Modal, CircularProgress, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

export const MaterialsTab = () => {
  const table = useRef(null);
  const uploadRef = useRef(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [createIsMandatory, setCreateIsMandatory] = useState(true);


  const [allTrainings, setAllTrainings] = useState([]);
  const [createSelectedTraining, setCreateSelectedTraining] = useState("");

  useEffect(() => {
    if (isCreateModalOpen) {
      const fetchTrainings = async () => {
        try {
          const response = await fetchData("/training/all", "GET", true);
          setAllTrainings(response.data.data || []);
        } catch (error) {
          console.error("Error fetching trainings:", error);
        }
      };
      fetchTrainings();
    }
  }, [isCreateModalOpen]);

  const handleOpenCreateModal = () => setCreateModalOpen(true);

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setCreateSelectedTraining("");
    setCreateIsMandatory(true);
    if (uploadRef.current) {
      uploadRef.current.clearFiles();
    }
  };

  const handleCreateSave = async () => {
    const files = uploadRef.current?.getSelectedFiles();

    if (!createSelectedTraining || createSelectedTraining === "") {
      Swal.fire({
        icon: "warning",
        title: intl.get("MissingInformation").d("Missing Information"),
        text: intl.get("PleaseSelectTraining").d("Please select a training.")
      });
      return;
    }

    if (!files || files.length === 0) {
      Swal.fire({
        icon: "warning",
        title: intl.get("MissingInformation").d("Missing Information"),
        text: intl.get("PleaseSelectFile").d("Please select a file.")
      });
      return;
    }

    setIsUploading(true);
    try {
      const file = files[0];
      const uploadRes = await fetchUrl(`/training_material/file_upload`, 'POST', true, file, false, true, null, null, 'multipart/form-data');

      if (!(uploadRes.status === HTTP_STATUS.CREATED_201 && uploadRes.data?.data?.pk)) {
        throw new Error(uploadRes.data?.detail || intl.get("FailedToUploadFile").d("Failed to upload file"));
      }
      const filePk = uploadRes.data.data.pk;

      const payload = {
        training_pk: parseInt(createSelectedTraining),
        file_pk: parseInt(filePk),
        is_mandatory: createIsMandatory
      };
      const linkRes = await fetchData('/training_material', 'POST', true, payload);
      if (linkRes.status !== HTTP_STATUS.CREATED_201) {
        throw new Error(linkRes?.data?.detail || intl.get("FailedToSaveTrainingMaterial").d("Failed to save training material."));
      }

      Swal.fire({ icon: "success", title: intl.get("Success").d("Success"), text: intl.get("TrainingMaterialCreated").d("Training material created successfully.") });
      table.current.updateTable();
      setCreateSelectedTraining("");
      setCreateIsMandatory(true);
      if (uploadRef.current) {
        uploadRef.current.clearFiles();
      }
      handleCloseCreateModal();
    } catch (error) {
      console.error("Error during upload:", error);
      Swal.fire({ icon: "error", title: intl.get("Error").d("Error"), text: error.message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (file_pk, filename) => {
    const downloadUrl = `/file/download/${file_pk}`;
    const token = getToken();
    try {
      const response = await fetch(downloadUrl, { method: 'GET', headers: { 'Authorization': `Bearer ${token.access_token}` } });
      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      Swal.fire(intl.get("DownloadError").d("Download Error"), err.message, "error");
    }
  }

  const header = [
    {
      label: intl.get("Training").d("Training"),
      column_name: "training",
      sortable: false,
      function: (training_obj) => training_obj?.name || '-',
    },
    {
      label: intl.get("Mandatory").d("Mandatory"),
      column_name: "is_mandatory",
      width: "130px",
      sortable: false,
      element: (row) => row.is_mandatory
        ? <Chip label={intl.get("Mandatory").d("Mandatory")} size="small" variant="outlined" />
        : <Chip label={intl.get("Optional").d("Optional")} size="small" variant="outlined" />,
    },
    {
      label: intl.get("FileName").d("File Name"),
      column_name: "file",
      sortable: false,
      function: (file_obj) => file_obj?.original_filename || '-',
    },
    { 
      label: intl.get("CreationDate").d("Creation Date"), 
      column_name: "created_on", 
      filter: true, 
      control: "date" 
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: "120px",
      sortable: false,
      element: (row) => (
        <DmsButtonGroup>
          <DmsButton
            icon={<DownloadIcon />}
            onClick={() => handleDownload(row.file_pk, row.file?.original_filename)}
            title={intl.get("DownloadFile").d("Download File")}
          />
          <DmsTableEditTrainingMaterialButton
            table={table}
            row={row}
            needs_authentication={true}
          />
          <DmsTableDeleteButton
            table={table}
            title={intl.get("DeleteTrainingMaterial").d("Delete Training Material")}
            text={intl.get("TrainingMaterialPage.DeleteButtonMessage").d(`The material will be unlinked. Are you sure?`)}
            url="/training_material/"
            row_pk={row.pk}
          />
        </DmsButtonGroup>
      ),
    },
  ];

  const toolbar = (
    <DmsFlexContainer>
      <DmsFlexContainer.Left>
        <DmsButton variant="primary" icon={<AddIcon />} onClick={handleOpenCreateModal} text={intl.get("AddMaterial").d("Add Material")} />
      </DmsFlexContainer.Left>
    </DmsFlexContainer>
  );

  return (
    <>
      <DmsTable header={header} managed={true} needs_authentication={true} ref={table} url={'/training_material/table'} toolbar={toolbar} />

      <Modal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        aria-labelledby="create-modal-title"
        aria-describedby="create-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="create-modal-title" variant="h6" component="h2" sx={{ mb: 1 }}>
            {intl.get("AddTrainingMaterial").d("Add Training Material")}
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {intl.get("SelectTrainingAndUploadFile").d("Select a training and upload a video file")}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <DmsCheckBox
              label={intl.get("Mandatory").d("Mandatory")}
              checked={createIsMandatory}
              onChange={(e) => setCreateIsMandatory(e?.target?.checked ?? e)}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {intl.get("StepSelectTraining").d("1. Select Training")} *
            </Typography>
            <FormControl fullWidth required error={!createSelectedTraining}>
              <InputLabel id="training-select-label">{intl.get("Training").d("Training")}</InputLabel>
              <Select
                labelId="training-select-label"
                value={createSelectedTraining}
                onChange={(e) => setCreateSelectedTraining(e.target.value)}
                label={intl.get("Training").d("Training")}
                disabled={allTrainings.length === 0}
              >
                {allTrainings.length === 0 ? (
                  <MenuItem disabled value="">{intl.get("NoTrainingsFound").d("No trainings found.")}</MenuItem>
                ) : (
                  allTrainings.map((training) => (
                    <MenuItem key={training.pk} value={training.pk}>
                      {training.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {allTrainings.length === 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {intl.get("NoTrainingsFound").d("No trainings found.")}{' '}
                <Link to={APP_ROUTES.TrainingCentral}>{intl.get("CreateOne").d("Create one?")}</Link>
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {intl.get("StepUploadFile").d("2. Upload Video File")} *
            </Typography>
            <DmsUploadOnly ref={uploadRef} autoUpload={false} accept={{ 'video/mp4': ['.mp4'] }} />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {intl.get("VideoFilesSupported").d("Video files supported (.MP4)")}
            </Typography>
          </Box>

          {isUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" color="textSecondary">
                {intl.get("UploadingFile").d("Uploading file, please wait...")}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <DmsButton variant="secondary" text={intl.get("Cancel").d("Cancel")} onClick={handleCloseCreateModal} disabled={isUploading} />
            <DmsButton variant="primary" onClick={handleCreateSave} text={intl.get("Save").d("Save")} disabled={isUploading} />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
